import React, { useCallback, useEffect, useRef, useState } from "react";

import { useStateRef } from "@/hooks";
import { Opinion, OpinionResult, OpinionVote } from "@/service/Model";
import { scaleRange } from "@/utils/transform";

import MeterOpinion from "./components/MeterOpinion";
import MeterPreview from "./components/MeterPreview";
import MeterResults from "./components/MeterResults";
import MeterTemplate from "./components/MeterTemplate";
import MeterUserIconHandle from "./components/MeterUserIconHandle";
import MeterVoteHandle from "./components/MeterVoteHandle";
import * as MeterConstants from "./MeterConstants";
import {
  buckets,
  centerPoint,
  getAnswerBucket,
  maxAnswer,
  minAnswer,
  percentageGutter,
  Point,
  Result,
} from "./MeterConstants";
import { MeterSVG } from "./styles";

type MeterProps = {
  interaction: Opinion;
  interactionResults: OpinionResult;
  previousAnswer?: number;
  icons: IIcons;
  tealiumConfig?: ITealium;
  onAnswer: (answer: number) => void;
};

const calculateResults = (values: number[]): Result[] => {
  const totalAmount = values.reduce((sum, val) => sum + val, 0);
  const results = values.map((amount, index) => ({
    amount,
    percent: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0,
    bucket: buckets[index],
    opacity: 1,
    size: 5,
  }));

  let maxPercent = MeterConstants.maxPercent;
  let minPercent = MeterConstants.minPercent;

  for (const result of results) {
    if (result.percent > maxPercent) maxPercent = result.percent;
    if (result.percent < minPercent) minPercent = result.percent;
  }

  for (const result of results) {
    if (result.percent > 0) {
      result.opacity = scaleRange(
        result.percent,
        minPercent,
        maxPercent,
        MeterConstants.minAlpha,
        MeterConstants.maxAlpha
      );
      result.size = scaleRange(
        result.percent,
        minPercent,
        maxPercent,
        MeterConstants.minSize(false),
        MeterConstants.maxSize
      );
    } else {
      result.opacity = MeterConstants.minAlpha;
      result.size = MeterConstants.minSize(false);
    }
  }

  return results;
};

const getUserIconPosition = (
  answer: number,
  results: Result[],
  opinions: OpinionVote[]
): Point => {
  const bucketOpinionsCount = opinions.filter((opinion) => {
    const opinionBucket = getAnswerBucket(
      scaleRange(opinion.opinion, 0, 180, minAnswer, maxAnswer)
    );
    return opinionBucket === answer;
  }).length;

  const result = results[answer];
  return {
    x: buckets[answer],
    y:
      centerPoint.y -
      (result?.size || 0) -
      percentageGutter -
      bucketOpinionsCount * 64,
  };
};

const USER_ICON_RESET_Y = 30;

const MeterAssembler: React.FC<MeterProps> = ({
  interaction,
  interactionResults,
  icons,
  tealiumConfig,
  previousAnswer,
  onAnswer,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Main state
  const [answer, setAnswer, answerRef] = useStateRef<number | undefined>(
    previousAnswer
  );
  const [values, setValues] = useState<number[]>(interactionResults.value);
  const [results, setResults] = useState<Result[]>(() =>
    calculateResults(interactionResults.value)
  );

  // UI state
  const [voteHandle, setVoteHandle] = useState<number>(
    previousAnswer ? buckets[previousAnswer] : centerPoint.x
  );
  const [voteHandleTouched, setVoteHandleTouched] = useState<boolean>(false);
  const [showOutro, setShowOutro] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [editorialOpinionIndex, setEditorialOpinionIndex] =
    useState<number>(-1);
  const [editorialOpinionOpen, setEditorialOpinionOpen] =
    useState<boolean>(false);
  const [voteUserIconHandle, setVoteUserIconHandle] = useState<
    Point | undefined
  >(
    previousAnswer
      ? getUserIconPosition(previousAnswer, results, interaction.opinions || [])
      : undefined
  );
  const [dropVoteUserIconHandle, setDropVoteUserIconHandle] =
    useState<boolean>(false);

  const hasVoted = answer !== undefined;
  const isInteractionEnded = MeterConstants.isEnded(interaction);

  const debouncedOnAnswer = useCallback(
    MeterConstants.debounce(() => {
      if (answerRef.current !== undefined) onAnswer(answerRef.current);
    }, 750),
    [answerRef, onAnswer]
  );

  useEffect(() => {
    if (hasVoted && answer !== undefined) {
      setVoteUserIconHandle(
        getUserIconPosition(answer, results, interaction.opinions || [])
      );
      setDropVoteUserIconHandle(false);
    }
  }, [results, hasVoted, answer, interaction.opinions]);

  const getSvgCoordinates = useCallback((x: number, y: number): DOMPoint => {
    return new DOMPoint(x, y).matrixTransform(
      svgRef.current?.getScreenCTM?.()?.inverse()
    );
  }, []);

  const getRescaledXCoordinate = useCallback(
    (x: number, y: number): number => {
      const svgCoordinates = getSvgCoordinates(x, y);
      return svgCoordinates.x - centerPoint.x;
    },
    [getSvgCoordinates]
  );

  const moveVoteHandle = useCallback(
    (x: number, y: number): void => {
      if (editorialOpinionIndex >= 0) return;
      const voteHandlePosition = getSvgCoordinates(x, y);
      setVoteHandle(voteHandlePosition.x);
    },
    [editorialOpinionIndex, getSvgCoordinates]
  );

  const handleVote = useCallback(
    (x: number, y: number): void => {
      const newValues = [...values];

      if (hasVoted && answer !== undefined) {
        newValues[answer] = Math.max(0, newValues[answer] - 1);
      }

      const newAnswerXCoordinate = getRescaledXCoordinate(x, y);
      const newAnswer = getAnswerBucket(newAnswerXCoordinate);
      newValues[newAnswer] = newValues[newAnswer] + 1;

      setAnswer(newAnswer);
      setVoteHandle(buckets[newAnswer]);
      setValues(newValues);
      setResults(calculateResults(newValues));
      setVoteUserIconHandle({
        x: getUserIconPosition(newAnswer, results, interaction.opinions || [])
          .x,
        y: USER_ICON_RESET_Y,
      });
      setDropVoteUserIconHandle(true);

      debouncedOnAnswer();
    },
    [
      values,
      hasVoted,
      answer,
      getRescaledXCoordinate,
      results,
      interaction.opinions,
      debouncedOnAnswer,
    ]
  );

  const resetVoteHandle = useCallback((): void => {
    setVoteHandle(
      hasVoted && answer !== undefined ? buckets[answer] : centerPoint.x
    );
    setVoteHandleTouched(false);
  }, [hasVoted, answer]);

  const handleOpenEditorialOpinion = useCallback((index: number): void => {
    setEditorialOpinionIndex(index);
    setEditorialOpinionOpen(true);
  }, []);

  const handleCloseEditorialOpinion = useCallback((): void => {
    setTimeout(() => setEditorialOpinionOpen(false), 3000);
    setEditorialOpinionIndex(-1);
  }, []);

  const handleMouseEvent = useCallback(
    (e: React.MouseEvent<SVGSVGElement>): void => {
      e.preventDefault();

      if (editorialOpinionIndex >= 0) {
        if (e.type === "mousedown") handleCloseEditorialOpinion();
        return;
      }

      switch (e.type) {
        case "mousedown":
          moveVoteHandle(e.clientX, e.clientY);
          setVoteHandleTouched(true);
          break;
        case "mousemove":
          if (!isInteractionEnded && e.buttons) {
            setVoteHandleTouched(true);
            moveVoteHandle(e.clientX, e.clientY);
          }
          break;
        case "mouseup":
          moveVoteHandle(e.clientX, e.clientY);
          handleVote(e.clientX, e.clientY);
          if (voteHandleTouched) setVoteHandleTouched(false);
          break;
        case "mouseleave":
          if (e.buttons) resetVoteHandle();
          break;
      }
    },
    [
      editorialOpinionIndex,
      handleCloseEditorialOpinion,
      moveVoteHandle,
      isInteractionEnded,
      handleVote,
      voteHandleTouched,
      resetVoteHandle,
    ]
  );

  useEffect(() => {
    if (!hasVoted || showOutro) return;
    const timeout = setTimeout(() => setShowOutro(true), 3500);
    return () => clearTimeout(timeout);
  }, [hasVoted, showOutro]);

  return (
    <MeterTemplate
      results={interactionResults}
      meter={interaction}
      icons={icons}
      hasVoted={hasVoted}
      onConfirmed={() => setShowPreview(true)}
      onOpinionClose={handleCloseEditorialOpinion}
      editorialOpinionIndex={editorialOpinionIndex}
      editorialOpinionOpen={editorialOpinionOpen}
      showOutro={showOutro}
      onOutroClose={() => setShowOutro(!showOutro)}
      tealiumConfig={tealiumConfig}
    >
      <MeterSVG
        onMouseUp={handleMouseEvent}
        onMouseMove={handleMouseEvent}
        onMouseDown={handleMouseEvent}
        onMouseLeave={handleMouseEvent}
        style={{ position: "relative", margin: "10px" }}
        ref={svgRef}
        viewBox={`0 -95 ${centerPoint.x * 2} 470`}
      >
        <MeterResults
          hasVoted={hasVoted}
          results={results}
          meter={interaction}
        />
        {showPreview && <MeterPreview hasVoted={hasVoted} results={results} />}
        {!isInteractionEnded && (
          <MeterVoteHandle
            icons={icons}
            hasVoted={hasVoted}
            voteHandle={voteHandle}
            voteHandleTouched={voteHandleTouched}
            onMoveVoteHandle={moveVoteHandle}
            onSnapVoteHandle={handleVote}
          />
        )}
        {!isInteractionEnded && (
          <MeterUserIconHandle
            icons={icons}
            voteUserIconPoint={voteUserIconHandle}
            dropVoteUserIcon={dropVoteUserIconHandle}
          />
        )}
        {hasVoted && (
          <MeterOpinion
            meter={interaction}
            results={results}
            onClickOpinion={handleOpenEditorialOpinion}
          />
        )}
      </MeterSVG>
    </MeterTemplate>
  );
};

export default MeterAssembler;
